import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import DashboardSidebar from '../../components/layout/DashboardSidebar';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../context/AuthContext';
import { userAPI, consultationAPI } from '../../lib/api';
import { formatDate, getInitials } from '../../lib/utils';
import { toast } from 'sonner';
import SettingsContent from './SettingsContent';
import {
    Search, Send, Briefcase, FileCheck, Calendar, Bell, Settings,
    Loader2, Building2, MapPin, PoundSterling, ExternalLink,
    ArrowRight, Crown, CheckCircle2, Download, Upload,
    TrendingUp, Clock, FileText, XCircle, AlertCircle,
    CreditCard, BarChart3, Eye, BookOpen
} from 'lucide-react';

// ============ FIND RECRUITERS TAB ============
const FindRecruitersTab = () => {
    const [agencies, setAgencies] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            try {
                const res = await userAPI.getViewedAgencies();
                setAgencies(res.data.agencies || []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    if (loading) return <LoadingState />;

    return (
        <div data-testid="find-recruiters-tab">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Search className="w-5 h-5 text-primary-600" />
                            Recruiters You've Viewed
                        </CardTitle>
                        <CardDescription>Agencies you've recently clicked on</CardDescription>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/agencies')} data-testid="browse-agencies-btn">
                        <Search className="w-4 h-4 mr-2" /> Browse All
                    </Button>
                </CardHeader>
                <CardContent>
                    {agencies.length === 0 ? (
                        <EmptyState
                            icon={Building2}
                            title="No recruiters viewed yet"
                            description="Browse agencies to see them here"
                            action={() => navigate('/agencies')}
                            actionLabel="Find Recruiters"
                        />
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {agencies.map((agency) => (
                                <div
                                    key={agency.agency_id}
                                    className="p-4 border rounded-xl hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer group"
                                    onClick={() => navigate(`/agencies/${agency.agency_id}`)}
                                    data-testid={`agency-card-${agency.agency_id}`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-12 h-12 rounded-xl bg-primary-50 border flex items-center justify-center shrink-0">
                                            {agency.logo_url ? (
                                                <img src={agency.logo_url} alt="" className="w-8 h-8 object-contain rounded" />
                                            ) : (
                                                <Building2 className="w-6 h-6 text-primary-600" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-neutral-900 truncate group-hover:text-primary-600 transition-colors">
                                                {agency.name}
                                            </h4>
                                            {agency.location && (
                                                <p className="text-sm text-neutral-500 flex items-center gap-1 mt-0.5">
                                                    <MapPin className="w-3 h-3" /> {agency.location}
                                                </p>
                                            )}
                                            {agency.industries?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {agency.industries.slice(0, 2).map((ind, i) => (
                                                        <Badge key={i} variant="outline" className="text-xs">{ind}</Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ============ AGENCY JOBS TAB ============
const AgencyJobsTab = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            try {
                const res = await userAPI.getViewedJobs();
                setJobs(res.data.jobs || []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    if (loading) return <LoadingState />;

    return (
        <div data-testid="agency-jobs-tab">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-primary-600" />
                            Jobs You've Viewed
                        </CardTitle>
                        <CardDescription>Job listings you've recently clicked on</CardDescription>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/jobs')} data-testid="browse-jobs-btn">
                        <Search className="w-4 h-4 mr-2" /> Browse All Jobs
                    </Button>
                </CardHeader>
                <CardContent>
                    {jobs.length === 0 ? (
                        <EmptyState
                            icon={Briefcase}
                            title="No jobs viewed yet"
                            description="Browse job listings to see them here"
                            action={() => navigate('/jobs')}
                            actionLabel="Browse Jobs"
                        />
                    ) : (
                        <div className="space-y-3">
                            {jobs.map((job) => (
                                <div
                                    key={job.job_id}
                                    className="flex items-start justify-between p-4 border rounded-xl hover:border-primary-300 hover:shadow-sm transition-all cursor-pointer group"
                                    onClick={() => window.open(job.apply_url || job.source_url, '_blank')}
                                    data-testid={`job-card-${job.job_id}`}
                                >
                                    <div className="flex items-start gap-4 flex-1">
                                        <div className="w-11 h-11 rounded-lg bg-primary-50 border flex items-center justify-center shrink-0">
                                            <Briefcase className="w-5 h-5 text-primary-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-semibold text-neutral-900 truncate group-hover:text-primary-600 transition-colors">
                                                {job.title}
                                            </h4>
                                            <p className="text-sm text-neutral-500 mt-0.5">
                                                {job.agency_name || job.company || 'Agency'}
                                            </p>
                                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                                                {job.location && (
                                                    <span className="flex items-center gap-1 text-xs text-neutral-500">
                                                        <MapPin className="w-3 h-3" /> {job.location}
                                                    </span>
                                                )}
                                                {job.salary && (
                                                    <span className="flex items-center gap-1 text-xs text-neutral-500">
                                                        <PoundSterling className="w-3 h-3" /> {job.salary}
                                                    </span>
                                                )}
                                                {job.job_type && (
                                                    <Badge variant="outline" className="text-xs capitalize">{job.job_type}</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <ExternalLink className="w-4 h-4 text-neutral-400 group-hover:text-primary-600 transition-colors mt-1" />
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ============ MULTI-APPLY TAB ============
const MultiApplyTab = ({ user }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            try {
                const res = await userAPI.getApplicationProgress();
                setOrders(res.data?.orders || []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    if (loading) return <LoadingState />;

    // Show only the most recent active order
    const currentOrder = orders.find(o => o.status === 'active' || o.status === 'in_progress') || orders[0];

    return (
        <div data-testid="multi-apply-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Send className="w-5 h-5 text-primary-600" />
                        Multi-Apply Plan
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {!currentOrder ? (
                        <div className="text-center py-8">
                            <Send className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                            <h3 className="font-medium text-neutral-900 mb-2">No Multi-Apply subscription</h3>
                            <p className="text-sm text-neutral-500 mb-4">Let us apply to jobs on your behalf with tailored applications</p>
                            <Button className="bg-primary-600 hover:bg-primary-700" onClick={() => navigate('/multi-apply')} data-testid="subscribe-multi-apply-btn">
                                <Crown className="w-4 h-4 mr-2" /> View Plans
                            </Button>
                        </div>
                    ) : (
                        <div data-testid={`multi-apply-order-${currentOrder.order_id}`}>
                            {/* Current Plan Name */}
                            <div className="mb-6">
                                <p className="text-sm text-neutral-500">Current Plan</p>
                                <h3 className="text-2xl font-bold text-neutral-900">{currentOrder.package_name || 'Multi-Apply'}</h3>
                            </div>

                            {/* Credit Summary */}
                            {(() => {
                                const total = currentOrder.total_applications || 100;
                                const sent = currentOrder.applications_used || 0;
                                const remaining = Math.max(0, total - sent);
                                const pct = total > 0 ? Math.round((sent / total) * 100) : 0;

                                return (
                                    <>
                                        <div className="grid grid-cols-3 gap-4 mb-6">
                                            <div className="p-4 bg-neutral-50 rounded-xl text-center border">
                                                <p className="text-2xl font-bold text-neutral-900">{total}</p>
                                                <p className="text-sm text-neutral-500">Total Credits</p>
                                            </div>
                                            <div className="p-4 bg-blue-50 rounded-xl text-center border border-blue-100">
                                                <p className="text-2xl font-bold text-blue-700">{sent}</p>
                                                <p className="text-sm text-blue-600">Jobs Applied</p>
                                            </div>
                                            <div className="p-4 bg-emerald-50 rounded-xl text-center border border-emerald-100">
                                                <p className="text-2xl font-bold text-emerald-700">{remaining}</p>
                                                <p className="text-sm text-emerald-600">Remaining</p>
                                            </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mb-6" data-testid="multi-apply-progress">
                                            <div className="flex justify-between text-sm mb-2">
                                                <span className="font-medium text-neutral-700">{sent} / {total} Submitted</span>
                                                <span className="text-neutral-500">{pct}%</span>
                                            </div>
                                            <Progress value={pct} className="h-3" />
                                        </div>
                                    </>
                                );
                            })()}

                            {/* Upgrade Button */}
                            <Button className="w-full bg-primary-600 hover:bg-primary-700" onClick={() => navigate('/multi-apply')} data-testid="upgrade-multi-apply-btn">
                                <TrendingUp className="w-4 h-4 mr-2" /> Click Here to Upgrade Plan
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ============ CV REVIEW TAB ============
const CVReviewTab = ({ user }) => {
    const [cvStatus, setCvStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            try {
                const res = await userAPI.getOptimisedCV();
                setCvStatus(res.data);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const res = await userAPI.downloadOptimisedCV();
            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = cvStatus?.filename || 'optimised_cv.pdf';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('CV downloaded');
        } catch (e) {
            toast.error('Failed to download CV');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) return <LoadingState />;

    return (
        <div data-testid="cv-review-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileCheck className="w-5 h-5 text-primary-600" />
                        CV Review
                    </CardTitle>
                    <CardDescription>Your CV optimisation status</CardDescription>
                </CardHeader>
                <CardContent>
                    {cvStatus?.has_optimised_cv ? (
                        <div className="space-y-4">
                            <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-xl">
                                <div className="flex items-center gap-3 mb-3">
                                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                    <div>
                                        <h3 className="text-lg font-semibold text-emerald-900">CV Status: Optimised</h3>
                                        <p className="text-sm text-emerald-700">
                                            Your CV has been professionally reviewed and optimised
                                        </p>
                                    </div>
                                </div>
                                {cvStatus.uploaded_at && (
                                    <p className="text-xs text-emerald-600 mb-4">
                                        Optimised on: {formatDate(cvStatus.uploaded_at)}
                                    </p>
                                )}
                                <Button
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    data-testid="download-optimised-cv-btn"
                                >
                                    {downloading ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Download className="w-4 h-4 mr-2" />
                                    )}
                                    Download Optimised CV
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="p-6 bg-neutral-50 border-2 border-dashed border-neutral-300 rounded-xl text-center">
                                <FileText className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                                <h3 className="font-medium text-neutral-900 mb-2">No optimised CV yet</h3>
                                <p className="text-sm text-neutral-500 mb-4">
                                    Want to optimise your CV? Submit it for professional review.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    {user?.cv_url ? (
                                        <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1.5">
                                            <CheckCircle2 className="w-4 h-4 mr-1" /> CV Uploaded
                                        </Badge>
                                    ) : (
                                        <Button variant="outline" onClick={() => navigate('/settings')} data-testid="upload-cv-for-review-btn">
                                            <Upload className="w-4 h-4 mr-2" /> Upload Your CV
                                        </Button>
                                    )}
                                    <Button className="bg-primary-600 hover:bg-primary-700" onClick={() => navigate('/cv-review')} data-testid="subscribe-cv-review-btn">
                                        <Crown className="w-4 h-4 mr-2" /> Get a Free CV Review
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ============ CONSULTATIONS TAB ============
const ConsultationsTab = () => {
    const [consultations, setConsultations] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const load = async () => {
            try {
                const res = await userAPI.getDashboard();
                setConsultations(res.data?.consultations || []);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    if (loading) return <LoadingState />;

    const now = new Date();
    const upcoming = consultations.filter(c => {
        const d = new Date(c.scheduled_date);
        return d >= now && c.status !== 'cancelled';
    });
    const past = consultations.filter(c => {
        const d = new Date(c.scheduled_date);
        return d < now || c.status === 'completed';
    });

    return (
        <div data-testid="consultations-tab">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary-600" />
                        Consultations
                    </CardTitle>
                    <CardDescription>Your booked consultation sessions</CardDescription>
                </CardHeader>
                <CardContent>
                    {consultations.length === 0 ? (
                        <div className="text-center py-8">
                            <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                            <h3 className="font-medium text-neutral-900 mb-2">No consultations booked</h3>
                            <p className="text-sm text-neutral-500 mb-4">Book a 1-on-1 session with a career advisor</p>
                            <Button className="bg-primary-600 hover:bg-primary-700" onClick={() => navigate('/consultation')} data-testid="book-consultation-btn">
                                <Calendar className="w-4 h-4 mr-2" /> Book a Consultation
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Upcoming */}
                            {upcoming.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-primary-600" /> Upcoming Consultations
                                    </h3>
                                    {upcoming.map((c) => {
                                        const date = new Date(c.scheduled_date);
                                        return (
                                            <div key={c.consultation_id} className="p-4 bg-primary-50 border border-primary-200 rounded-xl mb-3" data-testid={`consultation-${c.consultation_id}`}>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                                                            <Calendar className="w-6 h-6 text-primary-700" />
                                                        </div>
                                                        <div>
                                                            <h4 className="font-semibold text-primary-900 capitalize">
                                                                {c.consultation_type?.replace('_', ' ') || 'Consultation'}
                                                            </h4>
                                                            <p className="text-sm text-primary-700">
                                                                {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })} - {date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                            </p>
                                                            <p className="text-xs text-primary-600 mt-0.5">{c.duration_minutes} minutes</p>
                                                        </div>
                                                    </div>
                                                    <Badge className="bg-primary-100 text-primary-700">Upcoming</Badge>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Past */}
                            {past.length > 0 && (
                                <div>
                                    <h3 className="font-semibold text-neutral-600 mb-3">Past Consultations</h3>
                                    {past.map((c) => (
                                        <div key={c.consultation_id} className="p-4 bg-neutral-50 rounded-xl mb-2">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="font-medium text-neutral-700 capitalize">
                                                        {c.consultation_type?.replace('_', ' ')}
                                                    </h4>
                                                    <p className="text-sm text-neutral-500">{formatDate(c.scheduled_date)}</p>
                                                </div>
                                                <Badge variant="outline" className="capitalize">{c.status}</Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <Button variant="outline" className="w-full" onClick={() => navigate('/consultation')} data-testid="book-another-consultation-btn">
                                <Calendar className="w-4 h-4 mr-2" /> Book Another Consultation
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ============ NOTIFICATIONS TAB ============
const NotificationsTab = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await userAPI.getNotifications();
                setNotifications(res.data.notifications || []);
                setUnreadCount(res.data.unread_count || 0);
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        load();
    }, []);

    const handleMarkRead = async (id) => {
        try {
            await userAPI.markNotificationRead(id);
            setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (e) { console.error(e); }
    };

    const handleMarkAllRead = async () => {
        try {
            await userAPI.markAllNotificationsRead();
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
            toast.success('All notifications marked as read');
        } catch (e) { toast.error('Failed to update'); }
    };

    if (loading) return <LoadingState />;

    const getNotifIcon = (type) => {
        switch (type) {
            case 'credit_update': return <BarChart3 className="w-5 h-5 text-blue-600" />;
            case 'cv_optimised': return <FileCheck className="w-5 h-5 text-emerald-600" />;
            case 'consultation': return <Calendar className="w-5 h-5 text-purple-600" />;
            case 'subscription': return <CreditCard className="w-5 h-5 text-amber-600" />;
            default: return <Bell className="w-5 h-5 text-neutral-500" />;
        }
    };

    return (
        <div data-testid="notifications-tab">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="flex items-center gap-2">
                            <Bell className="w-5 h-5 text-primary-600" />
                            Notifications
                            {unreadCount > 0 && (
                                <Badge className="bg-red-500 text-white ml-2">{unreadCount}</Badge>
                            )}
                        </CardTitle>
                    </div>
                    {unreadCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                            Mark all as read
                        </Button>
                    )}
                </CardHeader>
                <CardContent>
                    {notifications.length === 0 ? (
                        <div className="text-center py-8">
                            <Bell className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                            <h3 className="font-medium text-neutral-900 mb-2">No notifications</h3>
                            <p className="text-sm text-neutral-500">You'll see updates about your applications and account here</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {notifications.map((notif) => (
                                <div
                                    key={notif.notification_id}
                                    className={`flex items-start gap-3 p-4 rounded-xl transition-colors cursor-pointer ${
                                        notif.read ? 'bg-neutral-50' : 'bg-primary-50 border border-primary-100'
                                    }`}
                                    onClick={() => !notif.read && handleMarkRead(notif.notification_id)}
                                    data-testid={`notification-${notif.notification_id}`}
                                >
                                    <div className="mt-0.5">{getNotifIcon(notif.type)}</div>
                                    <div className="flex-1">
                                        <h4 className={`text-sm font-medium ${notif.read ? 'text-neutral-700' : 'text-neutral-900'}`}>
                                            {notif.title}
                                        </h4>
                                        <p className="text-sm text-neutral-500 mt-0.5">{notif.message}</p>
                                        <p className="text-xs text-neutral-400 mt-1">{formatDate(notif.created_at)}</p>
                                    </div>
                                    {!notif.read && (
                                        <div className="w-2 h-2 rounded-full bg-primary-600 mt-2 shrink-0" />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

// ============ SHARED COMPONENTS ============
const LoadingState = () => (
    <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
    </div>
);

const EmptyState = ({ icon: Icon, title, description, action, actionLabel }) => (
    <div className="text-center py-12">
        <Icon className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
        <h3 className="font-medium text-neutral-900 mb-2">{title}</h3>
        <p className="text-sm text-neutral-500 mb-4">{description}</p>
        {action && (
            <Button className="bg-primary-600 hover:bg-primary-700" onClick={action}>
                {actionLabel}
            </Button>
        )}
    </div>
);

// ============ MAIN DASHBOARD PAGE ============
const DashboardPage = () => {
    const { user, loading: authLoading, refreshUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeSection, setActiveSection] = useState('find-recruiters');

    useEffect(() => {
        if (!authLoading && !user && !location.state?.user) {
            navigate('/');
        }
        // Redirect admins to their own dashboard
        if (!authLoading && user && (user.role === 'admin' || user.role === 'super_admin')) {
            navigate(user.role === 'super_admin' ? '/super-admin' : '/admin');
        }
    }, [user, authLoading, navigate, location.state]);

    if (authLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-10 h-10 text-primary-600 animate-spin" />
                </div>
            </Layout>
        );
    }

    const renderContent = () => {
        switch (activeSection) {
            case 'find-recruiters': return <FindRecruitersTab />;
            case 'multi-apply': return <MultiApplyTab user={user} />;
            case 'agency-jobs': return <AgencyJobsTab />;
            case 'cv-review': return <CVReviewTab user={user} />;
            case 'consultations': return <ConsultationsTab />;
            case 'notifications': return <NotificationsTab />;
            case 'settings': return <SettingsContent user={user} refreshUser={refreshUser} />;
            default: return <FindRecruitersTab />;
        }
    };

    return (
        <Layout>
            <div className="flex min-h-screen bg-neutral-50">
                <DashboardSidebar
                    userRole="user"
                    activeSection={activeSection}
                    onSectionChange={setActiveSection}
                />
                <div className="flex-1 overflow-auto w-full lg:w-auto">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pt-16 lg:pt-8">
                        {/* Header */}
                        <div className="flex items-center gap-4 mb-6">
                            <Avatar className="h-14 w-14">
                                <AvatarImage src={user?.picture} alt={user?.name} />
                                <AvatarFallback className="bg-primary-100 text-primary-700 text-lg">
                                    {getInitials(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div>
                                <h1 className="font-heading text-2xl font-bold text-neutral-900" data-testid="dashboard-heading">
                                    Welcome back, {user?.name?.split(' ')[0]}!
                                </h1>
                                <div className="flex items-center gap-2 mt-1">
                                    <Badge className={`capitalize ${
                                        user?.subscription_tier === 'pro' ? 'bg-amber-100 text-amber-700' :
                                        user?.subscription_tier === 'standard' ? 'bg-primary-100 text-primary-700' :
                                        'bg-neutral-100 text-neutral-700'
                                    }`}>
                                        {user?.subscription_tier || 'Free'} Plan
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        {renderContent()}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DashboardPage;
